import FormData from 'formdata-polyfill';
import qs from 'qs';
import request from 'superagent';
import Promise from 'promise-polyfill';
import Alert from 'Alert';

// polyfill for ie11/Edge
require('setimmediate');

// polyfill for ie11
if (!window.Promise) {
  window.Promise = Promise;
}

export default class FormSwiftAPI {
  constructor(url) {
    this.URL = url;
    this.errorMessage = '';
    this.whiteListQueryParams = [
      'documentType',
      'key',
      'ses',
    ];
    this.partialErrorCodes = [
      'association-error',
      'promo-code-error',
      'association-and-promo-error',
    ];
  }

  static getUrlSafeQueryParams(obj) {
    return Object.keys(obj).map(key => (
      `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`
    )).join('&');
  }

  static getRedirectPath(redirectURL) {
    // Take only the path component and redirect to that, otherwise we could
    // unintentionally redirect to a full url set by a hacker
    const parser = document.createElement('a');
    parser.href = redirectURL;
    const redirectPath = `${parser.pathname}${parser.search}${parser.hash}`;
    return redirectPath.charAt(0) === '/' ? redirectPath : `/${redirectPath}`;
  }

  static handleResponse(response, handleResponseData) {
    if (response.statusCode === 200 || response.statusCode === 201) {
      if (response.body.status === 'success') {
        return handleResponseData(response.body);
      }
      // Not a json response, just return the response and let the caller handle it
      return handleResponseData(response);
    } else if (response.statusCode === 403) {
      // Special Unauthorized case, let the caller handle it
      return handleResponseData(response);
    }
    return handleResponseData(response.body);
  }

  static buildPayload(formElement) {
    const formData = new FormData(formElement);
    const payload = {};

    // Serialize form data. This allows us to build up objects from the form when inputs have their
    // name attribute set to something like:
    // name='sender_info[name]'.

    // Unfortunately, the qs library is a bit overzealous and also tries to parse things that look
    // like querystrings to object attributes as well which isn't what we want, as we often put
    // url's in hidden form fields (for say redirect_url's). Thus, we tell QS to use a delimiter of
    // "NO_DELIMITER" instead of it's default "&" so it skips this not so helpful feature
    formData.forEach((formValue, formKey) => {
      const formEntryObj = qs.parse(`${formKey}=${formValue}`, { delimiter: 'NO_DELIMITER', decoder: str => (str) });

      const keys = Object.keys(formEntryObj);
      if (keys.length !== 1) {
        return;
      }
      const key = keys[0];

      let value;
      if (payload[key]) {
        value = Object.assign({}, payload[key], formEntryObj[key]);
      } else {
        value = formEntryObj[key];
      }

      payload[key] = value;
    });
    return payload;
  }

  static isOnResumeCoverLetterSite() {
    const rclSites = ['smartresumewizard.com', 'smartcoverletter.com', 'easyperfectresume.com', 'fastcoverletter.com'];
    // Get current domain without subdomains (specifically for local and staging environments)
    const domain = window.location.hostname.split('.').slice(-2).join('.');

    return rclSites.indexOf(domain) !== -1;
  }

  static handleNoUserIdForConfirmPaymentProcess() {
    const error = 'No user id';
    let checkoutPath;
    if (FormSwiftAPI.isOnResumeCoverLetterSite()) {
      checkoutPath = '/checkout.php';
    } else {
      checkoutPath = '/checkout';
    }
    window.location.href = FormSwiftAPI.getRedirectPath(`${checkoutPath}?error=${error}`);
  }

  static getUrlParamsForConfirmPaymentProcess(responseData) {
    const error = responseData.message || '';
    let params;
    if (responseData.status === 'success') {
      params = responseData.data.additional_vars;
    } else {
      params = responseData.data.redirect_params;
    }

    const key = params.key || '';
    const ses = params.ses || '';
    const documentType = params.document_type || '';

    let plan;

    if (params.plan === 0) {
      plan = params.plan;
    } else {
      plan = params.plan || '';
    }

    let urlParams = `key=${key}&ses=${ses}&documentType=${documentType}&plan=${plan}&error=${error}`;
    if (params.fax_number) {
      urlParams += `&fax_number=${params.fax_number}`;
    }

    if (responseData.status === 'success') {
      // Remove empty query params for confirmation page
      urlParams = urlParams.split('&').filter(param => param[param.length - 1] !== '=').join('&');
    }
    return urlParams;
  }

  filterQueryString(queryString) {
    if (!queryString) {
      return '';
    }
    const parser = document.createElement('a');
    parser.href = queryString;

    const queryParams = parser.search.substr(1).split('&');
    const filteredParams = [];

    for (let i = 0; i < queryParams.length; i += 1) {
      const queryKeyValue = queryParams[i];
      const queryKey = queryKeyValue.split('=')[0];
      if (this.whiteListQueryParams.indexOf(queryKey) >= 0) {
        filteredParams.push(queryKeyValue);
      }
    }

    if (filteredParams.length) {
      return `?${filteredParams.join('&')}`;
    }

    return '';
  }

  request(method, path, payload, handleResponseData) {
    if (method === 'GET') {
      return request
        .get(`${this.URL}${path}`)
        .query(payload)
        .then(
          resp => (
            FormSwiftAPI.handleResponse(resp, handleResponseData)
          ),
          err => (
            FormSwiftAPI.handleResponse(err.response, handleResponseData)
          ));
    }

    return request
      .post(`${this.URL}${path}`)
      .send(payload)
      .then(
        resp => (
          FormSwiftAPI.handleResponse(resp, handleResponseData)
        ),
        err => (
          FormSwiftAPI.handleResponse(err.response, handleResponseData)
        ));
  }

  createSession(payload, handleResult) {
    return this.request('POST', '/session', payload, handleResult);
  }

  updateSharedSessionContent(shareKey, payload, handleResult) {
    return this.request('POST', `/document-share/session/content/${shareKey}`, payload, handleResult);
  }

  updateSessionContent(sessionId, payload, handleResult) {
    return this.request('POST', `/session/content/${sessionId}`, payload, handleResult);
  }

  confirmPaymentProcess() {
    const userId = window.COOKIE.get('userid');
    const userToken = window.COOKIE.get('userToken');
    const search = window.location.search.substring(1);

    let queryParams = {};
    if (search) {
      queryParams = JSON.parse(`{"${decodeURI(search).replace(/"/g, '\\"').replace(/&/g, '","').replace(/=/g, '":"')}"}`);
    }

    // if user id does not exist, directly redirect to checkout
    if (!userId) {
      FormSwiftAPI.handleNoUserIdForConfirmPaymentProcess();
      return null;
    }

    queryParams.user_token = userToken;
    const apiPath = `/billing/${window.location.hostname}/confirm/${userId}`;

    const handleResponseData = (responseData) => {
      const redirectUrl = responseData.data.redirect_url;
      const urlParams = FormSwiftAPI.getUrlParamsForConfirmPaymentProcess(responseData);
      window.location.href = FormSwiftAPI.getRedirectPath(`${redirectUrl}?${urlParams}`);
      return null;
    };

    return this.request('POST', apiPath, queryParams, handleResponseData);
  }

  getCheckoutFormFields(userId, plan, queryParams, handleResult) {
    const self = this;
    const handleResponseData = (responseData) => {
      if (responseData.status === 'error') {
        let errorMessage;
        switch (responseData.message) {
          case 'Invalid oauth_timestamp':
            errorMessage = 'invalid-oauth-timestamp';
            break;
          default:
            errorMessage = responseData.message;
            break;
        }

        self.errorMessage = errorMessage;
        return handleResult(null);
      }

      const chargify = responseData.data.chargify;

      return handleResult(chargify);
    };

    return this.request('GET', `/billing/${window.location.hostname}/checkout-fields/${userId}/${plan}`, queryParams, handleResponseData);
  }

  shareRegister(payload, handleResult) {
    const handleResponseData = (responseData) => {
      if (responseData.status !== 'error' || this.partialErrorCodes.indexOf(responseData.message) > -1) {
        const user = responseData.data.user;
        const twoMonths = 60;

        window.COOKIE.set('userid', user.id, twoMonths);
        window.COOKIE.set('userToken', user.user_token, twoMonths);
        window.COOKIE.set('accountemail', encodeURIComponent(user.email), twoMonths);
      }
      return handleResult(responseData);
    };
    return this.request('POST', '/user/share-register', payload, handleResponseData);
  }

  handleShareRegister(formElement, promoCode, finish, handleResult, extraParams = null) {
    let payload = FormSwiftAPI.buildPayload(formElement);
    payload.promo_code = promoCode;
    payload.finish = finish;
    if (extraParams) {
      payload = {
        ...payload,
        ...extraParams,
      };
    }
    this.shareRegister(payload, handleResult);
  }

  register(payload) {
    const self = this;
    const forwardResponseData = responseData => (responseData);

    const modifiedPayload = Object.assign({}, payload);

    if (window.COOKIE.get('alias')) {
      modifiedPayload.alias = window.COOKIE.get('alias');
    }

    if (window.COOKIE.get('sessionId')) {
      modifiedPayload.anonymous_session_id = window.COOKIE.get('sessionId');
    }

    return this.request('POST', '/user/register', modifiedPayload, forwardResponseData)
    .then((responseData) => {
      if (responseData.status === 'error') {
        let errorMessage;
        switch (responseData.message) {
          case 'Invalid oauth_timestamp':
            errorMessage = 'invalid-oauth-timestamp';
            break;
          default:
            errorMessage = responseData.message;
            break;
        }
        self.errorMessage = errorMessage;
        return null;
      }

      const user = responseData.data.user;
      const twoMonths = 60;

      window.COOKIE.set('userid', user.id, twoMonths);
      window.COOKIE.set('userToken', user.user_token, twoMonths);
      window.COOKIE.set('accountemail', encodeURIComponent(user.email), twoMonths);

      return user;
    });
  }

  inlineRegister(payload) {
    return this.register(payload)
    .then((result) => {
      if (result) {
        const twoMonths = 60;
        const fwRegex = new RegExp(/[a-z0-9]{32}/gi);
        const isDocCreatedOnFWBuilder = fwRegex.test(payload.primary_document_type);
        window.COOKIE.set('documentCreated', payload.primary_document_type, twoMonths);
        if (isDocCreatedOnFWBuilder) {
          window.COOKIE.set('documentCreatedOnBuilder', 'freewrite', twoMonths);
        }
      }
      return result;
    });
  }

  handleRegister(formElement, e) {
    e.preventDefault();
    const payload = FormSwiftAPI.buildPayload(formElement);
    const self = this;
    return this.register(payload)
    .then((result) => {
      let redirectPath;
      if (result) {
        if (payload.redirect_url) {
          const filteredQueryString = self.filterQueryString(window.location.search);
          redirectPath = FormSwiftAPI.getRedirectPath(`${payload.redirect_url}${filteredQueryString}`);
        } else {
          redirectPath = '/myDocuments.php';
        }
        FormSwiftAPI.saveTrackingAndRedirect(result.id, redirectPath);
      } else {
        if (document.getElementById('alertMessageContainer')) {
          Alert.init('alertMessageContainer', {
            type: 'error',
            code: self.errorMessage,
            email: payload.email,
          });
        }

        document.getElementById('submitRegister').innerText = 'Create My Account';
        document.getElementById('submitRegister').removeAttribute('disabled');
        document.getElementById('submitRegister').classList.remove('disabled');
      }
    });
  }

  handleInlineRegister(formElement, e) {
    e.preventDefault();
    const payload = FormSwiftAPI.buildPayload(formElement);

    const self = this;
    return this.inlineRegister(payload)
    .then((result) => {
      if (result) {
        const additionalVars = {
          ses: payload.session_hash,
          key: payload.session_id,
        };

        if (payload.documentType) {
          additionalVars.documentType = payload.documentType;
        }

        let redirectPath;
        if (payload.redirect_url) {
          const parser = document.createElement('a');
          parser.href = payload.redirect_url;
          const redirectQueryParams = qs.parse(parser.search.substring(1));
          const updatedRedirectQueryParams = Object.assign({}, additionalVars, redirectQueryParams);
          const urlParams = FormSwiftAPI.getUrlSafeQueryParams(updatedRedirectQueryParams);
          redirectPath = FormSwiftAPI.getRedirectPath(`${parser.pathname}?${urlParams}${parser.hash}`);
        } else {
          let redirectFile;
          if (FormSwiftAPI.isOnResumeCoverLetterSite()) {
            redirectFile = 'upgradePricing.php';
          } else {
            redirectFile = 'upgrade-pricing';
          }
          redirectPath = `/${redirectFile}?${FormSwiftAPI.getUrlSafeQueryParams(additionalVars)}`;
        }

        FormSwiftAPI.saveTrackingAndRedirect(result.id, redirectPath);
      } else {
        Alert.init('alertMessageContainer', {
          type: 'error',
          code: self.errorMessage,
          email: payload.email,
        });

        document.getElementById('submitRegister').innerText = 'Create My Account';
        document.getElementById('submitRegister').removeAttribute('disabled');
        document.getElementById('submitRegister').classList.remove('disabled');
      }
    });
  }

  static saveTrackingAndRedirect(userId, redirectPath) {
    // We make uadPage the current page that saveCookieToDB is called from
    // based on historical calls of this function
    const uadPage = `${window.location.host}${window.location.pathname}`;

    const callBack = () => {
      window.location.assign(redirectPath);
    };

    const isTrackingSaved = window._trackSourceData.saveCookieToDB(uadPage, userId, callBack);

    // If tracking is not saved, we still need to redirect since
    // `saveCookieToDB` does not redirect if it does not need to save cookie.
    // https://github.com/kingstreetlabs/KingStreetLabs/issues/3825 will handle
    // cleaning up this logic
    if (!isTrackingSaved) {
      callBack();
    }
  }

  createDocumentShare(payload, handleResponse) {
    return this.request('POST', '/document-share', payload, handleResponse);
  }

  finishDocumentShare(shareKey, handleResponse) {
    return this.request('POST', `/document-share/${shareKey}/finish`, null, handleResponse);
  }

  finishDocumentShareForSender(sessionId, sessionHash, handleResponse) {
    const userId = window.COOKIE.get('userid');
    const userToken = window.COOKIE.get('userToken');
    const payload = {
      session_hash: sessionHash,
      user_id: userId,
      user_token: userToken,
    };
    return this.request('POST', `/session/${sessionId}/finish`, payload, handleResponse);
  }

  cancelDocumentShare(sessionId, handleResponse) {
    const userId = window.COOKIE.get('userid');
    const userToken = window.COOKIE.get('userToken');
    const payload = {
      user_id: userId,
      user_token: userToken,
    };
    return this.request('POST', `/document-share/cancel-requests/${sessionId}`, payload, handleResponse);
  }

  getLTVValue(documentType, plan, handleResponse) {
    const payload = {
      document_type: documentType,
      plan,
    };
    return this.request('GET', '/ltv-value', payload, handleResponse);
  }

  deactivateSessions(sessionIds, handleResponse) {
    const userId = window.COOKIE.get('userid');
    const userToken = window.COOKIE.get('userToken');
    const payload = {
      session_ids: sessionIds,
      user_token: userToken,
    };
    return this.request('POST', `/user/${userId}/sessions/deactivate`, payload, handleResponse);
  }

  saveAdditionalUserData(userId, userToken, fromPage, additionalDataToSave, handleResponse) {
    const payload = {
      data: additionalDataToSave,
      from_page: fromPage,
      user_token: userToken,
    };
    return this.request('POST', `/user/${userId}/additional-data/save`, payload, handleResponse);
  }
}



// WEBPACK FOOTER //
// ./src-assets/js/formswift-api.js